import { createContext, useCallback, useContext } from 'react';
import { css } from '@emotion/react';
import { SettingType_Enum } from '@weave/schema-gen-ts/dist/schemas/messaging/settings/v1/settings.pb';
import { InboxView, ThreadReadStatus } from '@weave/schema-gen-ts/dist/schemas/sms/data/v4/sms_service.pb';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { ClientSettingsApi } from '@frontend/api-client-settings';
import { FaxQueries } from '@frontend/api-fax';
import { FormsSubmission } from '@frontend/api-forms';
import { MessagingSettingsV1 } from '@frontend/api-messaging-settings';
import { PhoneCallsQueries } from '@frontend/api-phone-calls';
import { SMSDataV4 } from '@frontend/api-sms-data';
import { getUser, isWeaveUser, filterLocationIdsByACLs } from '@frontend/auth-helpers';
import { Dashboard } from '@frontend/dashboard';
import { useFeatureAccess } from '@frontend/feature-access';
import { useTranslation } from '@frontend/i18n';
import { Icon, type IconName } from '@frontend/icons';
import { useMessagingFeaturesEnabled } from '@frontend/messaging-enabled-hook';
import { MultiSettingsHooks } from '@frontend/multi-settings';
import {
  openSchedulePulse,
  SchedulePulseTab,
  useGetScheduleRequestAndAlertsCount,
  useIsScheduleSmallModeWindow,
} from '@frontend/schedule';
import { useScopedAppFlagStore, useAppScopeStore } from '@frontend/scope';
import { NOT_SUPPORTED_IN_SCOPE_MESSAGE, useScopePolicy, type EntryPointSurfaceId } from '@frontend/scope-access';
import { dispatchTopBarTraySelectionEvent } from '@frontend/shared';
import { useTaskCenterStore } from '@frontend/task-center-scope';
import { useGetTaskCenterCustomizationFlagValue } from '@frontend/task-center-scope';
import { useTeamChatSelector } from '@frontend/team-chat-new';
import { Link } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import { Text, NotificationBadge, NakedButton } from '@frontend/design-system';

interface AllAppsModule {
  showHeader?: boolean;
  handleModalClose?: () => void;
}

const AllAppsModuleContext = createContext<AllAppsModule['handleModalClose'] | undefined>(undefined);

export const useAllAppsModule = () => useContext(AllAppsModuleContext);

export const AllAppsModule = ({ showHeader = true, handleModalClose }: AllAppsModule) => {
  const { t } = useTranslation('dashboard');

  return (
    <AllAppsModuleContext.Provider value={handleModalClose}>
      {showHeader && <Dashboard.Module.Header title={t('All Apps')} />}
      <Dashboard.Module.Content>
        <div
          css={css`
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            row-gap: ${theme.spacing(2.5)};
            padding: ${theme.spacing(1)};
          `}
        >
          <Home />
          <PhoneAppFeatureButton />
          <VoicemailAppFeatureButton />
          <MessagesAppFeatureButton />
          <AppFeatureButton
            icon='contacts'
            to='/contacts/all-contacts'
            label={t('Contacts')}
            surfaceId='dashboard.contacts'
          />
          <CalendarAppFeatureButton />
          <TeamChatFeatureButton />
          <FormsAppFeatureButton />
          <AppFeatureButton
            icon='reviews-dash'
            to='/reviews/all-reviews'
            label={t('Reviews')}
            surfaceId='dashboard.reviews'
          />
          <AnalyticsFeatureButton />
          <PaymentsAppFeatureButton />
          <FaxAppFeatureButton />
          <TasksAppFeatureButton />
          <QuickfillFeatureButton />
          <MarketingFeatureButton />
        </div>
      </Dashboard.Module.Content>
    </AllAppsModuleContext.Provider>
  );
};

const AnalyticsFeatureButton = () => {
  const { t } = useTranslation('analytics');

  return (
    <AppFeatureButton
      icon='analytics'
      label={t('Analytics')}
      to='/analytics/dashboard'
      surfaceId='dashboard.analytics'
    />
  );
};

const PaymentsAppFeatureButton = () => {
  const { t } = useTranslation('dashboard');

  return (
    <AppFeatureButton icon='payments' to='/payments/invoices' label={t('Payments')} surfaceId='dashboard.payments' />
  );
};

const TasksAppFeatureButton = () => {
  const { isTaskCenterEnabled: isTaskCenterAvailable } = useGetTaskCenterCustomizationFlagValue();

  const { t } = useTranslation('task-center');
  const { setIsTaskCenterOpen } = useTaskCenterStore(['isTaskCenterOpen', 'setIsTaskCenterOpen']);

  if (!isTaskCenterAvailable) return null;

  return (
    <AppFeatureButton
      onClick={() => setIsTaskCenterOpen(true)}
      icon='mark-done'
      label={t('Task Center')}
      surfaceId='dashboard.task-center'
    />
  );
};

const QuickfillFeatureButton = () => {
  const { t } = useTranslation('dashboard');

  return (
    <AppFeatureButton
      onClick={() => dispatchTopBarTraySelectionEvent('quickfill')}
      icon='quickfill'
      label={t('Waitlist')}
      surfaceId='dashboard.quickfill'
    />
  );
};

const CalendarAppFeatureButton = () => {
  const { t } = useTranslation('dashboard');
  const { scheduleAlertsCount, scheduleRequestCount } = useGetScheduleRequestAndAlertsCount();
  const { isSmallModeWindow: isScheduleSmallModeWindow } = useIsScheduleSmallModeWindow();

  const totalNotificationCount = scheduleAlertsCount + (scheduleRequestCount?.total || 0);

  const handleOpenSchedulePulse = useCallback(() => {
    openSchedulePulse(SchedulePulseTab.AppointmentList);
  }, []);

  return (
    <AppFeatureButton
      count={totalNotificationCount}
      icon='calendar'
      onClick={isScheduleSmallModeWindow ? undefined : handleOpenSchedulePulse}
      to={isScheduleSmallModeWindow ? '/schedule/calendar' : undefined}
      label={t('Schedule')}
      surfaceId={isScheduleSmallModeWindow ? 'dashboard.schedule.route' : 'dashboard.schedule.pulse'}
    />
  );
};

const PhoneAppFeatureButton = () => {
  const { t } = useTranslation('dashboard');

  return <AppFeatureButton icon='phone' to='/calls/call-history' label={t('Phone')} surfaceId='dashboard.calls' />;
};

const TeamChatV2RemasteredButton = () => {
  const { t } = useTranslation('dashboard');
  const unreadMentionsCount = useTeamChatSelector((ctx) => ctx?.unreadMentionsCount);
  const unreadMessagesCount = useTeamChatSelector((ctx) => ctx?.unreadMessagesCount);
  const openTeamChat = useTeamChatSelector((ctx) => ctx?.openTeamChat);

  return (
    <AppFeatureButton
      icon='chat'
      count={(unreadMentionsCount ?? 0) + unreadMessagesCount}
      onClick={() => {
        openTeamChat();
      }}
      label={t('Team Chat')}
      surfaceId='dashboard.team-chat'
    />
  );
};

const VoicemailAppFeatureButton = () => {
  const { t } = useTranslation('dashboard');
  const voicemailsUnreadCount = PhoneCallsQueries.useVoicemailsUnreadCount();

  return (
    <AppFeatureButton
      icon='voicemail'
      to='/calls/voicemails'
      label={t('Voicemails')}
      count={voicemailsUnreadCount}
      surfaceId='dashboard.voicemails'
    />
  );
};

const Home = () => {
  const { t } = useTranslation('dashboard');

  return <AppFeatureButton icon='home' to='/home/dashboard' label={t('Home')} surfaceId='dashboard.home' />;
};

const MessagesAppFeatureButton = () => {
  const { t } = useTranslation('dashboard');
  const messagingEnabled = useMessagingFeaturesEnabled();

  const { selectedLocationIds, selectedOrgId } = useAppScopeStore();
  const locationIdsWithInboxACL = filterLocationIdsByACLs({
    locationIds: selectedLocationIds,
    acls: Permission.SMS_INBOX_MANAGE,
  });

  const { getFeatureFlagValue, isFlagStoreLoaded } = useScopedAppFlagStore();

  const { aggregatedValue: autoMessageInboxVisibility, isLoading: isAutoMessageInboxSettingsLoading } =
    MultiSettingsHooks.useBooleanClientSetting({
      set: ClientSettingsApi.querySetKeys.sets.autoMessageInboxVisibility,
      key: ClientSettingsApi.querySetKeys.keys.autoMessageInboxVisibility,
      defaultValue: 'false',
      locationIds: selectedLocationIds,
    });
  const avoidLastAutomated = autoMessageInboxVisibility !== true;

  const actionableTabVisibilityFF = getFeatureFlagValue('nwx:inbox-actionable-tab');
  const { data: actionableTabIsEnabled, isLoading: isEnableActionableTabLoading } =
    MessagingSettingsV1.Queries.useListQuery(
      {
        settingType: SettingType_Enum.INBOX_LOCATION_SETTING,
        orgId: selectedOrgId,
        locationIds: selectedLocationIds,
      },
      {
        enabled: !!actionableTabVisibilityFF,
        select: (data) => {
          return data.settings.some(({ setting }) => {
            if ('inboxLocationSetting' in setting) {
              return setting.inboxLocationSetting.enableActionableTab ?? false;
            }
            return false;
          });
        },
      }
    );

  const isActionableFeatureEnabled = actionableTabIsEnabled && actionableTabVisibilityFF;

  const { data: inboxUnreadCount } = SMSDataV4.Queries.useCountThreadsQuery({
    httpOptions: {
      onFetchError: (error) => {
        if (error.message.includes('too many')) {
          console.warn(error);
        } else {
          throw error;
        }
      },
    },
    request: {
      locationIds: locationIdsWithInboxACL,
      filter: {
        inboxView: isActionableFeatureEnabled ? InboxView.ACTIONABLE : InboxView.UNSPECIFIED,
        statuses: isActionableFeatureEnabled ? undefined : [ThreadReadStatus.NEW],
        excludeAutomatedLastMessage: isActionableFeatureEnabled ? undefined : avoidLastAutomated,
      },
    },
    options: {
      select: (data) => data.count,
      enabled:
        !!locationIdsWithInboxACL[0] &&
        !isEnableActionableTabLoading &&
        !isAutoMessageInboxSettingsLoading &&
        isFlagStoreLoaded &&
        messagingEnabled,
    },
  });

  if (!messagingEnabled) return null;

  return (
    <AppFeatureButton
      count={inboxUnreadCount}
      icon='messaging'
      to='/messages/inbox'
      label={t('Messages')}
      surfaceId='dashboard.messages'
    />
  );
};

const FaxAppFeatureButton = () => {
  const { t } = useTranslation('dashboard');
  const { selectedLocationIds } = useAppScopeStore();
  const { hasAccess: hasFaxAccess } = useFeatureAccess('fax-2-0', Permission.FAX_INBOX_MANAGE);

  const { data: faxUnreadCount } = FaxQueries.useCountFaxes(selectedLocationIds, {
    enabled: hasFaxAccess && selectedLocationIds.length > 0,
  });

  if (!hasFaxAccess) return null;

  return (
    <AppFeatureButton
      count={faxUnreadCount?.faxCount}
      icon='fax'
      to='/fax/inbox'
      label={t('Fax')}
      surfaceId='dashboard.fax'
    />
  );
};

const TeamChatFeatureButton = () => {
  const user = getUser();
  const weaveUser = isWeaveUser();
  const { getCustomizationFlagValue } = useScopedAppFlagStore();
  const hasChatCustomizationFlag = getCustomizationFlagValue('chat');

  const shouldHaveChat = hasChatCustomizationFlag && user && !weaveUser;

  if (!shouldHaveChat) return null;

  return <TeamChatV2RemasteredButton />;
};

const FormsAppFeatureButton = () => {
  const { t } = useTranslation('dashboard');
  const formsSubmissionCount = FormsSubmission.useSubmissionsUnreadCount();

  return (
    <AppFeatureButton
      count={formsSubmissionCount.data?.count}
      icon='forms'
      to='/forms/submissions'
      label={t('Forms')}
      surfaceId='dashboard.forms'
    />
  );
};

const MarketingFeatureButton = () => {
  const { t } = useTranslation('dashboard');

  return <AppFeatureButton icon='bulk-messaging' label={t('Marketing')} to='/bulk' surfaceId='dashboard.marketing' />;
};

type AppFeatureButtonProps = {
  icon: IconName;
  label: string;
  to?: string;
  show?: boolean;
  size?: 'large' | 'small';
  count?: number;
  onClick?: () => void;
  surfaceId?: EntryPointSurfaceId;
};

export const AppFeatureButton = ({
  icon,
  label,
  to,
  show,
  size = 'large',
  count,
  onClick,
  surfaceId,
}: AppFeatureButtonProps) => {
  const handleModalClose = useAllAppsModule();
  const { canAccessEntryPointSurface } = useScopePolicy();

  const hasScopeAccess = surfaceId ? canAccessEntryPointSurface(surfaceId) : true;
  const isDisabled = !hasScopeAccess;
  const resolvedDisabledReason = surfaceId && !hasScopeAccess ? NOT_SUPPORTED_IN_SCOPE_MESSAGE : undefined;

  const handleClick = useCallback(() => {
    if (isDisabled) {
      return;
    }

    onClick?.();
    handleModalClose?.();
  }, [handleModalClose, isDisabled, onClick]);

  if (show === false) return null;

  const shouldRenderAsButton = !!onClick || isDisabled || !to;
  const isInsideNav = !!handleModalClose;

  const buttonStyles = css`
    width: ${size === 'large' ? '72px' : '60px'};
    height: ${size === 'large' ? '72px' : '60px'};
    background-color: ${theme.colors.primary50};
    color: ${theme.colors.white};
    border-radius: ${theme.borderRadius.full};
    display: flex;
    justify-content: center;
    align-items: center;
    transition: 0.3s ease;
    position: relative;
    :hover,
    :focus {
      box-shadow: ${theme.shadows.floating};
    }
    &:hover,
    &:active {
      transform: scale(1.05);
      box-shadow: ${theme.shadows.heavy};
    }

    &:active {
      transform: scale(0.95);
    }

    ${isDisabled &&
    css`
      opacity: 0.6;
      cursor: not-allowed;
      transform: none;
    `}
  `;

  const buttonContent = (
    <>
      <Icon name={icon} size={32} />
      {!!count && (
        <NotificationBadge truncateCount css={{ position: 'absolute', right: 0, top: 0 }}>
          {count}
        </NotificationBadge>
      )}
    </>
  );

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: ${theme.spacing(1)};
        align-items: center;
        position: relative;
      `}
    >
      {shouldRenderAsButton ? (
        <NakedButton
          disabled={isDisabled}
          data-trackingid={`${isInsideNav ? 'nav-' : ''}all-apps-feature-${icon}-button`}
          onClick={handleClick}
          title={resolvedDisabledReason}
          css={buttonStyles}
        >
          {buttonContent}
        </NakedButton>
      ) : (
        <Link
          to={to}
          data-trackingid={`${isInsideNav ? 'nav-' : ''}all-apps-feature-${icon}-button`}
          onClick={handleClick}
          title={resolvedDisabledReason}
          css={buttonStyles}
        >
          {buttonContent}
        </Link>
      )}
      <Text size='small'>{label}</Text>
    </div>
  );
};
